import { api } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@iq/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { CheckCircle2, ClipboardList, FileText } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type FeedbackTask = NonNullable<Awaited<ReturnType<typeof fetchTasks>>>['tasks'][number];
type FeedbackTaskSource = {
  name: string;
  kbId?: string;
  documentId?: string;
  ref?: string;
};

async function fetchTasks() {
  const { data, error } = await api.GET('/feedback/tasks');
  if (error) throw error;
  return data;
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function sourceDocumentsForTask(task: FeedbackTask): FeedbackTaskSource[] {
  if (task.sourceDocuments?.length) return task.sourceDocuments;
  return task.sourceDocumentTitles?.map((name) => ({ name })) ?? [];
}

export const Route = createFileRoute('/_authed/tasks')({
  component: TasksRoute,
});

function TasksRoute() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('open');
  const [selectedTask, setSelectedTask] = useState<FeedbackTask | null>(null);
  const tasksQuery = useQuery({
    queryKey: ['feedback', 'tasks'],
    queryFn: fetchTasks,
  });

  const markDone = useMutation({
    mutationFn: async (eventId: string) => {
      const { data, error } = await api.PATCH('/feedback/tasks/{eventId}/status', {
        params: { path: { eventId } },
        body: { status: 'kb_updated' },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      setSelectedTask(null);
      return queryClient.invalidateQueries({ queryKey: ['feedback', 'tasks'] });
    },
  });

  const tasks = tasksQuery.data?.tasks ?? [];
  const { openTasks, doneTasks } = useMemo(() => {
    return {
      openTasks: tasks.filter((task) => task.status !== 'kb_updated'),
      doneTasks: tasks.filter((task) => task.status === 'kb_updated'),
    };
  }, [tasks]);

  if (tasksQuery.isPending) {
    return (
      <main className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-4 py-8">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </main>
    );
  }

  if (tasksQuery.isError || !tasksQuery.data) {
    return (
      <main className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-4 py-8">
        <p role="alert" className="text-destructive text-sm">
          {t('tasks.error')}
        </p>
      </main>
    );
  }

  return (
    <main
      className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-8"
      data-testid="tasks-route"
    >
      <header className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <span className="rounded-xl border bg-card p-2 text-primary">
            <ClipboardList className="size-5" aria-hidden />
          </span>
          <div>
            <h1 className="text-2xl font-semibold">{t('tasks.title')}</h1>
            <p className="text-muted-foreground text-sm">{t('tasks.subtitle')}</p>
          </div>
        </div>
      </header>

      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          setSelectedTask(null);
          setActiveTab(value);
        }}
        className="flex flex-col gap-4"
      >
        <TabsList className="h-auto w-full flex-wrap justify-start gap-1 bg-muted/60 p-1 sm:w-fit">
          <TabsTrigger value="open" className="gap-2">
            {t('tasks.tabs.open')}
            <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
              {openTasks.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="done" className="gap-2">
            {t('tasks.tabs.done')}
            <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
              {doneTasks.length}
            </Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="open" className="mt-0">
          <TaskList
            tasks={openTasks}
            emptyText={t('tasks.empty.open')}
            markDonePending={markDone.isPending}
            selectedTask={selectedTask}
            onSelectTask={setSelectedTask}
            onOpenChange={(open) => {
              if (!open) setSelectedTask(null);
            }}
            onMarkDone={(eventId) => markDone.mutate(eventId)}
          />
        </TabsContent>
        <TabsContent value="done" className="mt-0">
          <TaskList
            tasks={doneTasks}
            emptyText={t('tasks.empty.done')}
            selectedTask={selectedTask}
            onSelectTask={setSelectedTask}
            onOpenChange={(open) => {
              if (!open) setSelectedTask(null);
            }}
          />
        </TabsContent>
      </Tabs>
    </main>
  );
}

function TaskList({
  tasks,
  emptyText,
  markDonePending,
  selectedTask,
  onSelectTask,
  onOpenChange,
  onMarkDone,
}: {
  tasks: FeedbackTask[];
  emptyText: string;
  markDonePending?: boolean;
  selectedTask: FeedbackTask | null;
  onSelectTask: (task: FeedbackTask) => void;
  onOpenChange: (open: boolean) => void;
  onMarkDone?: (eventId: string) => void;
}) {
  if (tasks.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground text-sm">
          {emptyText}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="grid gap-2">
        {tasks.map((task) => (
          <TaskRow key={task.id} task={task} onSelect={() => onSelectTask(task)} />
        ))}
      </div>
      <TaskDetailsDialog
        task={selectedTask}
        open={Boolean(selectedTask)}
        onOpenChange={onOpenChange}
        {...(markDonePending !== undefined ? { markDonePending } : {})}
        {...(onMarkDone ? { onMarkDone } : {})}
      />
    </>
  );
}

function TaskRow({ task, onSelect }: { task: FeedbackTask; onSelect: () => void }) {
  const { t } = useTranslation();
  const sources = sourceDocumentsForTask(task);
  return (
    <button
      type="button"
      className="group rounded-xl border bg-card/70 px-4 py-3 text-left transition-colors hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onClick={onSelect}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 rounded-lg bg-muted p-1.5 text-muted-foreground group-hover:text-foreground">
          <FileText className="size-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <p className="line-clamp-1 break-words font-medium text-sm [overflow-wrap:anywhere]">
              {task.rewrittenQuery ?? task.question}
            </p>
            <Badge
              variant={task.status === 'kb_updated' ? 'secondary' : 'outline'}
              className="w-fit shrink-0"
            >
              {t(`admin.feedback.status.${task.status}`)}
            </Badge>
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-muted-foreground text-xs">
            <span>{t(`admin.feedback.types.${task.type}`)}</span>
            <span>{formatDate(task.createdAt)}</span>
            <span>{t('tasks.sourcesCount', { count: sources.length })}</span>
          </div>
          {task.answerText ? (
            <p className="line-clamp-1 break-words text-muted-foreground text-xs [overflow-wrap:anywhere]">
              {task.answerText}
            </p>
          ) : null}
        </div>
      </div>
    </button>
  );
}

function TaskDetailsDialog({
  task,
  open,
  onOpenChange,
  markDonePending,
  onMarkDone,
}: {
  task: FeedbackTask | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  markDonePending?: boolean;
  onMarkDone?: (eventId: string) => void;
}) {
  const { t } = useTranslation();
  if (!task) return null;
  const sources = sourceDocumentsForTask(task);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-[min(900px,calc(100vw-2rem))] overflow-y-auto">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2 pr-8">
            <DialogTitle className="break-words text-lg leading-7 [overflow-wrap:anywhere]">
              {task.rewrittenQuery ?? task.question}
            </DialogTitle>
            <Badge variant={task.status === 'kb_updated' ? 'secondary' : 'outline'}>
              {t(`admin.feedback.status.${task.status}`)}
            </Badge>
          </div>
          <DialogDescription>
            {t(`admin.feedback.types.${task.type}`)} · {formatDate(task.createdAt)}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <TaskSection title={t('tasks.fields.question')} value={task.question} />
          {task.answerText ? (
            <TaskSection title={t('tasks.fields.answer')} value={task.answerText} />
          ) : null}
          {task.feedbackText ? (
            <TaskSection title={t('tasks.fields.feedback')} value={task.feedbackText} />
          ) : null}
          {task.feedbackReason ? (
            <TaskSection
              title={t('tasks.fields.reason')}
              value={t(`admin.feedback.reasons.${task.feedbackReason}`)}
            />
          ) : null}
          <section className="space-y-2">
            <h2 className="font-medium text-muted-foreground text-sm">
              {t('tasks.fields.sources')}
            </h2>
            {sources.length > 0 ? (
              <ul className="list-disc space-y-1 pl-5 text-sm">
                {sources.map((source) => (
                  <li
                    key={`${source.kbId ?? ''}:${source.documentId ?? ''}:${source.ref ?? ''}:${source.name}`}
                    className="break-words [overflow-wrap:anywhere]"
                  >
                    {source.name}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground text-sm">{t('tasks.fields.noSources')}</p>
            )}
          </section>
        </div>
        {task.status !== 'kb_updated' && onMarkDone ? (
          <DialogFooter>
            <Button type="button" onClick={() => onMarkDone(task.id)} disabled={markDonePending}>
              <CheckCircle2 className="mr-2 size-4" aria-hidden />
              {t('tasks.actions.markKbUpdated')}
            </Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function TaskSection({ title, value }: { title: string; value: string }) {
  return (
    <section className="space-y-2">
      <h2 className="font-medium text-muted-foreground text-sm">{title}</h2>
      <p className="whitespace-pre-wrap break-words rounded-lg border bg-muted/30 p-3 text-sm leading-6 [overflow-wrap:anywhere]">
        {value}
      </p>
    </section>
  );
}
